/**
 * Finding Your Voice — From the Courtyard (tip of the day)
 *
 * Short, in-voice, actionable tips from the three guides. Consumed by
 * CourtyardContext's day-keyed rotation and rendered on the dashboard.
 *
 * Rules: period-pure guide voice (no modern jargon), one action per tip,
 * every tip points somewhere in the app, and NO story spoilers — tips can
 * surface before a learner has read any module.
 */

export const TIPS = [
  // ── Philemon — the inner work ──
  {
    id: 'philemon_01',
    characterId: 'philemon',
    text: 'Before your next difficult conversation, ask what the fear is guarding. The answer is usually your reason to speak.',
    link: { label: 'Sit with the Fear Companion', to: '/toolkit/fear-companion' },
  },
  {
    id: 'philemon_02',
    characterId: 'philemon',
    text: 'The voice that says you are not ready: notice whose voice it actually is. It is rarely yours, and it was never invited.',
    link: { label: 'Evening 1: Why Speaking Feels Scary', to: '/evenings/1' },
  },
  {
    id: 'philemon_03',
    characterId: 'philemon',
    text: 'I remember when a single pause felt like falling. It is not. A pause is the room breathing with you.',
    link: { label: 'Evening 6: Your Body Already Knows', to: '/evenings/6' },
  },
  {
    id: 'philemon_04',
    characterId: 'philemon',
    text: 'Before you stand up, ask what in this is yours to say. Hold one sentence back from the borrowed voice and say it as you would at your own table.',
    link: { label: 'Evening 2: Who Are You When You Speak?', to: '/evenings/2' },
  },
  {
    id: 'philemon_05',
    characterId: 'philemon',
    text: 'If your hands tremble today, let them. The trembling can come along. It does not get to decide.',
    link: { label: 'Evening 1: Why Speaking Feels Scary', to: '/evenings/1' },
  },
  {
    id: 'philemon_06',
    characterId: 'philemon',
    text: 'Name what you are feeling before you shape what you are saying. The feeling is information about what matters.',
    link: { label: 'Open the Feeling Compass', to: '/toolkit/feeling-compass' },
  },
  {
    id: 'philemon_07',
    characterId: 'philemon',
    text: 'If your first try at saying what you feel comes out as the speech, try again. The second attempt is usually shorter, and yours.',
    link: { label: 'Evening 5: Emotion Is Information', to: '/evenings/5' },
  },
  {
    id: 'philemon_08',
    characterId: 'philemon',
    text: 'I have never once been steady before I spoke. I pour the cup with the hands I have.',
    link: { label: 'Evening 8: The Courage to Be Heard', to: '/evenings/8' },
  },

  // ── Xenia — the relational work ──
  {
    id: 'xenia_01',
    characterId: 'xenia',
    text: 'Before you speak today, learn one thing the other person is worried about. A worry spoken to is attention earned.',
    link: { label: "Evening 4: The Listener's Mindset", to: '/evenings/4' },
  },
  {
    id: 'xenia_02',
    characterId: 'xenia',
    text: 'In your next conversation, ask one question you do not know the answer to, and wait for the whole answer.',
    link: { label: "Evening 4: The Listener's Mindset", to: '/evenings/4' },
  },
  {
    id: 'xenia_03',
    characterId: 'xenia',
    text: 'From where you stand you cannot know why a face is frowning. Until it shows you otherwise, give it the kindest plausible explanation.',
    link: { label: "Evening 4: The Listener's Mindset", to: '/evenings/4' },
  },
  {
    id: 'xenia_04',
    characterId: 'xenia',
    text: 'Make room today for someone who has not spoken. The most powerful thing you can do in a room is make space for another voice.',
    link: { label: 'Return to the Courtyard', to: '/courtyard' },
  },
  {
    id: 'xenia_05',
    characterId: 'xenia',
    text: 'Every audience carries three silent questions: why should I care, can I trust you, and what would you have me do? Answer one early.',
    link: { label: 'Build an Audience Portrait', to: '/toolkit/audience-portrait' },
  },
  {
    id: 'xenia_06',
    characterId: 'xenia',
    text: 'Speak to one person at a time, even in a crowd. A room is only ever faces, taken one by one.',
    link: { label: "Evening 4: The Listener's Mindset", to: '/evenings/4' },
  },
  {
    id: 'xenia_07',
    characterId: 'xenia',
    text: 'If you cannot yet say it warmly, wait. Words keep. Rooms remember.',
    link: { label: 'Evening 5: Emotion Is Information', to: '/evenings/5' },
  },
  {
    id: 'xenia_08',
    characterId: 'xenia',
    text: 'Attention always has somewhere to live. Give it the room today, or it will live in you.',
    link: { label: "Evening 4: The Listener's Mindset", to: '/evenings/4' },
  },

  // ── Theron — the practical work ──
  {
    id: 'theron_01',
    characterId: 'theron',
    text: 'One sentence. If you cannot say what you want in one sentence, you are not ready to want it aloud.',
    link: { label: 'Run the Message Distillery', to: '/toolkit/message-distillery' },
  },
  {
    id: 'theron_02',
    characterId: 'theron',
    text: 'Three words on the back of your hand will save you when your mind goes white. Choose them before you need them.',
    link: { label: 'Open the Message Architect', to: '/toolkit/message-architect' },
  },
  {
    id: 'theron_03',
    characterId: 'theron',
    text: 'Feet under your hips, shoulders down, three slow breaths. It takes less time than pouring wine, and you will speak the steadier for it.',
    link: { label: 'Do the Presence Prep', to: '/toolkit/presence-prep' },
  },
  {
    id: 'theron_04',
    characterId: 'theron',
    text: 'Never improvise the last thing they will hear. Craft your closing word for word; gratitude can wait for the end.',
    link: { label: 'Evening 7: Structure Sets You Free', to: '/evenings/7' },
  },
  {
    id: 'theron_05',
    characterId: 'theron',
    text: 'Thank-yous are throat-clearing. Start with the thing itself. Your first breath should earn your second.',
    link: { label: 'Evening 7: Structure Sets You Free', to: '/evenings/7' },
  },
  {
    id: 'theron_06',
    characterId: 'theron',
    text: 'Interesting is not the same as necessary. Cut the cargo that is not for this voyage.',
    link: { label: 'Run the Message Distillery', to: '/toolkit/message-distillery' },
  },
  {
    id: 'theron_07',
    characterId: 'theron',
    text: 'Stillness is not emptiness. Stand still for ten heartbeats today and watch a room come to you.',
    link: { label: 'Evening 6: Your Body Already Knows', to: '/evenings/6' },
  },
  {
    id: 'theron_08',
    characterId: 'theron',
    text: 'Practice is the only teacher whose fees are fair. Do the small brave thing today, not the large one someday.',
    link: { label: 'Evening 8: The Courage to Be Heard', to: '/evenings/8' },
  },
];
